// Which side of its trigger a popover panel hangs from.
//
// Every popover in the console anchors at `left: 0` of its trigger, which is
// right until the trigger sits near the right edge of the window: the panel
// then runs off-screen and the operator reads a half-cut control (the ab-tests
// date filter, pinned to the right of its own header row, showed the date
// pickers as a clipped "dd/"). Absolutely-positioned panels are not clamped by
// anything, so the flip has to be decided from measurements.
//
// Pure so the decision is testable without a layout engine: the caller measures
// (trigger rect, panel width, viewport width) and this says which edge to pin.

/** Breathing room kept between a panel and the window edge, in px. */
const VIEWPORT_MARGIN = 8;

export interface PanelAlignInput {
  /** Trigger's left edge, viewport coordinates (`getBoundingClientRect`). */
  triggerLeft: number;
  /** Trigger's right edge, viewport coordinates. */
  triggerRight: number;
  /** The panel's own width. 0 before it has been laid out (or in jsdom). */
  panelWidth: number;
  /** `window.innerWidth`. */
  viewportWidth: number;
}

/** True when the panel should hang from the trigger's RIGHT edge instead.
 *
 *  Only flips when the default alignment actually overflows AND the flipped one
 *  fits: a panel wider than the space either side stays where it started rather
 *  than trading a clipped right edge for a clipped left one, where the labels
 *  and the presets live. An unmeasured panel (width 0) never flips. */
export function shouldAlignEnd({
  triggerLeft,
  triggerRight,
  panelWidth,
  viewportWidth,
}: PanelAlignInput): boolean {
  if (panelWidth <= 0) return false;
  const overflowsRight = triggerLeft + panelWidth > viewportWidth - VIEWPORT_MARGIN;
  const fitsLeftward = triggerRight - panelWidth >= VIEWPORT_MARGIN;
  return overflowsRight && fitsLeftward;
}
